import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NAME_PATTERN } from '../../constants/validation-patterns';
import { AppState } from '../../app.service';
import { ProfileService } from '../../../services/profile.service';
import { ModalService } from '../../../services/modal.service';
import { UserService } from '../../../services/user.service';
import { IUser } from '../../../interfaces/user';

/** MOCK_API: Replace with real IUserProfile from API */
export interface ChangeNameModalData {
  user: {
    customerId?: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  customClass?: string;
}

@Component({
  selector: 'app-change-name-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './change-name-modal.component.html',
  styleUrl: './change-name-modal.component.scss'
})
export class ChangeNameModalComponent {
  public nameForm: FormGroup;
  public namePattern = NAME_PATTERN;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChangeNameModalData,
    private dialogRef: MatDialogRef<ChangeNameModalComponent>,
    private appState: AppState,
    private profileService: ProfileService,
    private modalService: ModalService,
    private translate: TranslateService,
    private userService: UserService,
  ) {
    this.nameForm = new FormGroup({
      firstName: new FormControl(this.data?.user?.firstName ?? '', [
        Validators.required,
        Validators.pattern(this.namePattern)
      ]),
      lastName: new FormControl(this.data?.user?.lastName ?? '', [
        Validators.required,
        Validators.pattern(this.namePattern)
      ])
    });
  }

  updateName(): void {
    this.nameForm.markAllAsTouched();
    if (this.nameForm.invalid) {
      return;
    }

    this.appState.loading = true;
    const updatedInfo: any = {
      firstName: this.nameForm.controls['firstName'].value.trim(),
      lastName: this.nameForm.controls['lastName'].value.trim(),
    };

    this.profileService.updateUserProfile(updatedInfo).then(() => {
      this.userService.setUser({
        firstName: updatedInfo.firstName,
        lastName: updatedInfo.lastName
      });
      this.closeDialog({ ...this.data.user, ...updatedInfo });
      this.modalService.successModal(
        {
          msg: this.translate.instant('success-modal.name-successfully-updated'),
          buttonText: this.translate.instant('success-modal.done'),
          customClass: 'secondary-success-modal no-massage',
        }
      );
      this.appState.loading = false;
    }).catch(() => {
      this.modalService.failureModal(
        {
          msg: this.translate.instant('failure-modal.request-cannot-be-processed'),
          buttonText: this.translate.instant('failure-modal.retry'),
          customClass: 'secondary-fail-modal',
          title: this.translate.instant('failure-modal.something-went-wrong'),
        }
      );
      this.appState.loading = false;
    });
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?: { firstName: string; lastName: string; customerId?: string; email?: string }): void {
    this.beforeClose();
    this.dialogRef.close(res);
  }

  cancel(): void {
    this.closeDialog();
  }
}
